import { EthereumProvider } from '@manifoldxyz/manifold-sdk';

export const fetchWithTimeout = (url: string, options: any, timeout = 7000) => {
  return Promise.race([
    fetch(url, options),
    new Promise<Response>((_, reject) => setTimeout(() => reject(new Error('timeout')), timeout))
  ]);
};

export default class APIHelpers {
  public backendBaseUrl: string;

  static accessToken?: string;

  async getHeaders(oauthToken?: string) {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    } as any;
    if (oauthToken) {
      headers['Authorization'] = `Bearer ${oauthToken}`;
    }

    return headers;
  }

  constructor(backendBaseUrl: string) {
    this.backendBaseUrl = backendBaseUrl;
  }

  async fetch(path: string, options: any = {}, oauthToken?: string) {
    try {
      let url = this.backendBaseUrl + path;

      if (options.query) {
        const queryString = new URLSearchParams(options.query).toString();
        url = url + '?' + queryString;
      }

      const requestOptions = {
        method: 'GET',
        headers: await this.getHeaders(oauthToken),
        ...options
      };

      const response = await fetch(url, requestOptions);
      await this.handleOAuthExpired(response);

      if (response.status >= 400 && response.status <= 500) {
        throw response;
      }

      try {
        return await response.json();
      } catch {
        return [];
      }
    } catch (error: any) {
      if (error instanceof Response) {
        // Process if response object
        let errorResponse;
        try {
          errorResponse = await error.json();
        } catch {
          // Could not decode
          errorResponse = new Error(`Non-200 status: ${error.status}: ${await error.text()}`);
        }
        throw errorResponse;
      } else {
        throw error;
      }
    }
  }

  async put(path: string, options: any = {}, oauthToken?: string) {
    try {
      let url = this.backendBaseUrl + path;

      if (options.query) {
        const queryString = new URLSearchParams(options.query).toString();
        url = url + '?' + queryString;
      }

      const requestOptions = {
        method: 'PUT',
        headers: await this.getHeaders(oauthToken),
        ...options
      } as any;

      if (options.body) {
        requestOptions.body = JSON.stringify(options.body);
      }

      const response = await fetch(url, requestOptions);
      await this.handleOAuthExpired(response);
      if (response.status !== 200) {
        throw response;
      }
      const result = await response.json();

      return result;
    } catch (error: any) {
      if (error instanceof Response) {
        // Process if response object
        let errorResponse;
        try {
          errorResponse = await error.json();
        } catch {
          // Could not decode
          errorResponse = new Error(`Non-200 status: ${error.status}: ${await error.text()}`);
        }
        throw errorResponse;
      } else {
        throw error;
      }
    }
  }

  async post(path: string, options: any = {}, oauthToken?: string) {
    try {
      let url = this.backendBaseUrl + path;

      if (options.query) {
        const queryString = new URLSearchParams(options.query).toString();
        url = url + '?' + queryString;
      }
      const requestOptions = {
        method: 'POST',
        headers: await this.getHeaders(oauthToken),
        ...options
      } as any;

      if (options.body) {
        requestOptions.body = JSON.stringify(options.body);
      }

      const response = await fetch(url, requestOptions);
      await this.handleOAuthExpired(response);

      if (response.status !== 200) {
        throw response;
      }
      const result = await response.json();

      return result;
    } catch (error: any) {
      if (error instanceof Response) {
        // Process if response object
        let errorResponse;
        try {
          errorResponse = await error.json();
        } catch {
          // Could not decode
          errorResponse = new Error(`Non-200 status: ${error.status}: ${await error.text()}`);
        }
        throw errorResponse;
      } else {
        throw error;
      }
    }
  }

  async delete(path: string, options: any = {}) {
    try {
      let url = this.backendBaseUrl + path;

      if (options.query) {
        const queryString = new URLSearchParams(options.query).toString();
        url = url + '?' + queryString;
      }

      const headers = await this.getHeaders();
      delete headers['Content-Type'];

      const requestOptions = {
        method: 'DELETE',
        headers,
        ...options
      } as any;

      const response = await fetch(url, requestOptions);
      await this.handleOAuthExpired(response);
      if (response.status !== 200) {
        throw response;
      }
      const result = await response.json();

      return result;
    } catch (error: any) {
      if (error instanceof Response) {
        // Process if response object
        let errorResponse;
        try {
          errorResponse = await error.json();
        } catch {
          // Could not decode
          errorResponse = new Error(`Non-200 status: ${error.status}: ${await error.text()}`);
        }
        throw errorResponse;
      } else {
        throw error;
      }
    }
  }

  private handleOAuthExpired = async (res: Response) => {
    if (res.status === 401) {
      await EthereumProvider.disconnect();
      console.warn('Session Expired');
      throw new Error('Session Expired');
    }
  };
}
